import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LoadingButton } from '@lf/ui-primitives';
import { approveChildLink, rejectChildLink } from '../api/lfApiChildLink';
import lfApi from '@lf/api-client/lfApi';

function ChildLinkPage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [requestInfo, setRequestInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        async function fetchPendingLinks() {
            try {
                const response = await lfApi.get('/user/child-links');
                if (response.status === 200) {
                    const match = response.data.pending_requests.find(r => r.token === token);
                    setRequestInfo(match || null);
                }
            } catch (err) {
                console.error('Error fetching link info:', err);
            }
            setIsLoading(false);
        }
        fetchPendingLinks();
    }, [token]);

    async function handleApprove() {
        setError('');
        const response = await approveChildLink(token);
        if (response && response.data && response.data.success) {
            setResult('approved');
        } else {
            setError(response?.data?.error || 'Failed to approve. Please try again.');
        }
    }

    async function handleReject() {
        setError('');
        const response = await rejectChildLink(token);
        if (response && response.data && response.data.success) {
            setResult('rejected');
        } else {
            setError(response?.data?.error || 'Failed to reject. Please try again.');
        }
    }

    if (isLoading) {
        return <div className="my-8 text-center">Loading...</div>;
    }

    if (result === 'approved') {
        return (
            <div className="flex mx-auto max-w-screen-lg my-8">
                <div className="inline-flex flex-col items-center border border-gray-300 bg-white rounded-2xl mx-auto py-6 px-6 sm:px-20">
                    <h2 className="text-lg mb-4">Account Linked</h2>
                    <p className="text-sm text-gray-600 mb-4">
                        {requestInfo?.child_first_name}'s account has been linked to yours.
                    </p>
                    <button
                        className="bg-blue-500 text-white py-2 px-4 rounded"
                        onClick={() => navigate('/')}>
                        Go to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    if (result === 'rejected') {
        return (
            <div className="flex mx-auto max-w-screen-lg my-8">
                <div className="inline-flex flex-col items-center border border-gray-300 bg-white rounded-2xl mx-auto py-6 px-6 sm:px-20">
                    <h2 className="text-lg mb-4">Request Rejected</h2>
                    <p className="text-sm text-gray-600 mb-4">
                        The link request has been rejected.
                    </p>
                    <button
                        className="bg-blue-500 text-white py-2 px-4 rounded"
                        onClick={() => navigate('/')}>
                        Go to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    if (!requestInfo) {
        return (
            <div className="flex mx-auto max-w-screen-lg my-8">
                <div className="inline-flex flex-col items-center border border-gray-300 bg-white rounded-2xl mx-auto py-6 px-6 sm:px-20">
                    <h2 className="text-lg mb-4">Link Request Not Found</h2>
                    <p className="text-sm text-gray-600 mb-4">
                        This link request may have already been resolved or the link is invalid.
                    </p>
                    <button
                        className="bg-blue-500 text-white py-2 px-4 rounded"
                        onClick={() => navigate('/')}>
                        Go to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex mx-auto max-w-screen-lg my-8">
            <div className="inline-flex flex-col items-center border border-gray-300 bg-white rounded-2xl mx-auto py-6 px-6 sm:px-20">
                <h2 className="text-lg mb-4">Child Link Request</h2>
                <p className="text-sm text-gray-600 mb-2">
                    <strong>{requestInfo.child_first_name}</strong> ({requestInfo.child_username}, Grade {requestInfo.child_grade_level}) wants to link their account to yours.
                </p>
                <p className="text-xs text-gray-400 mb-6">
                    Requested on {new Date(requestInfo.inserted_at).toLocaleDateString()}
                </p>

                {error && <div className="text-red-500 text-sm mb-4">{error}</div>}

                <div className="flex gap-4">
                    <LoadingButton
                        text="Approve"
                        onClick={handleApprove}
                    />
                    <button
                        className="bg-gray-300 text-gray-700 py-2 px-4 rounded"
                        onClick={handleReject}>
                        Reject
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ChildLinkPage;
